import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Paper, Text, ActionIcon, Tooltip, Badge } from '@cowbell/ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGrid2,
  faEnvelope,
  faBracketsCurly,
  faReply,
  faReplyAll,
  faArrowsRotate,
  faChevronDown,
  faChevronUp,
  faPen,
  faChartLine,
} from '@fortawesome/pro-light-svg-icons';
import type {
  EnrichedCmailMessage,
  MessageInfo,
} from '../../../api/cmail/cmail.dto';
import { JsonViewerModal } from './CmailDetailsResolver/CmailDetails/JsonViewerModal';
import CmailComposer from './CmailComposer';
import { useGetSubmissionByMsgId } from '../../../components/hooks/queries/submissions/useGetSubmission';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';
import useNotifications from '../../../_lib/notifications/useNotifications';
import { reprocessEmail } from '../../../api/cmail.api';
import { CmailReprocessConfirmationModalConfig } from './CmailReprocessConfirmationModal';
import { Modal as CustomModal } from '../../../components/modals/v2/helpers/v2.modal.helpers';
import { CmailStatusBanner } from './CmailStatusBanner';
import { CmailStatusModal } from './CmailStatusModal';
import { useLatestDraft } from '../hooks/useLatestDraft';
import { useRequestTracking } from '../hooks/useRequestTracking';
import { useCmailContext } from './CmailStateProvider';
import { AssignedToCell } from './AssignedToCell';
import { CmailClaimLink } from './CmailClaimLink';
import { useExtractionPanelOptional } from './ExtractionPanelContext';
import { buildSubmissionDetailsLink } from '../../submissions/utils';
import {
  getEmailTypeColor,
  normalizeMessageCategoryForUI,
} from '../utils/emailTypeUtils';
import classes from './CmailDetailsPanel.module.css';
import { distinctSubSubCategories } from '../utils/subCategoryUtils';

interface ThreadContext {
  isThreaded: boolean;
  isExpanded: boolean;
  onToggleExpansion: () => void;
  messageIndex: number;
  isCurrentMessage: boolean;
  totalMessages: number;
  depth?: number;
}

interface CmailDetailsPanelProps {
  message: EnrichedCmailMessage;
  fullMessageDetails: MessageInfo;
  threadContext?: ThreadContext | null;
}

const getLabelColor = (label: string): string => {
  switch (label) {
    case 'Complete':
      return 'var(--mantine-color-green-6)';
    case 'Incomplete':
      return 'var(--mantine-color-dimmed)';
    case 'Escalated':
      return 'var(--mantine-color-red-6)';
    case 'On Hold':
      return 'var(--mantine-color-yellow-6)';
    default:
      return 'var(--mantine-color-dimmed)';
  }
};

const getCategoryChipStyle = (category: string): React.CSSProperties => {
  let color: string;
  switch (category) {
    case 'SUBMISSION':
      color = 'var(--cmail-cowbell-green2)';
      break;
    case 'CLAIMS':
    case 'Claims':
    case 'Claimsops':
      color = 'var(--mantine-color-red-6)';
      break;
    case 'BOR':
      color = 'var(--mantine-color-blue-6)';
      break;
    case 'CRS':
    case 'crs':
    case 'Crs':
    case 'SUBJECTIVITY':
    case 'Subjectivity':
      color = 'var(--mantine-color-violet-6)';
      break;
    case 'SPAM':
    case 'Spam':
      color = 'var(--mantine-color-red-8)';
      break;
    case 'MTE':
    case 'CERT_REQUEST':
    case 'CertRequest':
      color = 'var(--mantine-color-blue-4)';
      break;
    case 'RENEWALS':
    case 'RENEWAL':
    case 'Renewal':
      color = 'var(--cmail-cowbell-green2)';
      break;
    case 'CANCELLATION':
    case 'Cancellation':
      color = 'var(--mantine-color-red-4)';
      break;
    case 'API':
      color = 'var(--mantine-color-violet-4)';
      break;
    case 'GENERAL':
    case 'General':
      color = 'var(--mantine-color-text)';
      break;
    case 'COMPLIANCE':
    case 'Compliance':
      color = 'var(--mantine-color-yellow-6)';
      break;
    case 'FINANCE':
    case 'Finance':
      color = 'var(--mantine-color-cyan-6)';
      break;
    default:
      color = 'var(--mantine-color-dimmed)';
  }

  return {
    color,
    border: `1px solid ${color}`,
    backgroundColor: 'transparent',
  };
};

const getCategoryAbbreviation = (category: string) => {
  const abbreviationMap: Record<string, string> = {
    'Management Liability': 'ML',
    'Directors & Officers': 'D&O',
    'Directors and Officers': 'D&O',
    'Employment Practices Liability': 'EPL',
    Crime: 'Crime',
    Fiduciary: 'Fiduciary',
  };

  return abbreviationMap[category] || category;
};

const getDisplayCategory = (msgCategory: string) => {
  if (msgCategory === 'CRS') {
    return 'CRS';
  }
  if (msgCategory === 'BOR') {
    return 'BOR';
  }
  if (msgCategory === 'Claimsops') {
    return 'Claims';
  }
  return msgCategory;
};

/**
 * The categories ds-docmessage actually creates request tracking for.
 *
 * <p>Derived from its call sites, not guessed: `initializeRequestTracking` /
 * `initializeRequestTrackingWithItemTrackings` are invoked for exactly these four --
 * `EmailProcessingService` (LOSSRUNS, CLAIMS, CLAIMS_OPS) and `BorService` (BOR). A category
 * missing here has its banner silently suppressed even though the backend recorded every item,
 * which is how a claims-ops email showed three tracked invoices in the API and nothing on screen.
 *
 * <p>CRS was listed and never tracked: it reaches `MessageCategory.CRS` only in `OpenAiService`
 * and creates no tracking, so the fetch it triggered could never return anything. Add it back
 * when ds-docmessage starts recording it.
 */
export const REQUEST_TRACKING_CATEGORIES = [
  'LOSSRUNS',
  'BOR',
  'CLAIMS',
  'CLAIMS_OPS',
];

/**
 * Whether a message's category is one ds-docmessage records request tracking for.
 *
 * <p>Compared through `normalizeMessageCategoryForUI` on both sides, because the two lists are
 * written in different alphabets. The constant above is deliberately in the backend's form --
 * membership is ds-docmessage's decision and the list has to be readable against
 * `MessageCategory` -- but `cmail.api.ts` normalises every message before the UI sees it, so
 * `msgCategory` arrives as `Claims`, `ClaimsOps`, `LossRuns`.
 *
 * <p>A direct `includes` therefore matched only BOR, which is the one member that normalises to
 * itself. The other three -- the ones whose banner this list exists to show -- never matched, so
 * a claims-ops email showed three tracked invoices in the API and nothing on screen.
 */
export const tracksRequests = (category?: string): boolean => {
  const tracked = REQUEST_TRACKING_CATEGORIES.map(
    normalizeMessageCategoryForUI
  );
  return tracked.includes(normalizeMessageCategoryForUI(category));
};

const InProgressIcon = ({ color }: { color: string }) => (
  <Box component="svg" className={classes.inProgressIcon} viewBox="0 0 24 24">
    <circle
      cx="12"
      cy="12"
      r="10"
      fill="none"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeDasharray="50 10"
      opacity="0.6"
    />
    <circle cx="12" cy="12" r="4" fill={color} />
  </Box>
);

export const CmailDetailsPanel: React.FC<CmailDetailsPanelProps> = ({
  message,
  fullMessageDetails,
  threadContext,
}) => {
  const [isJsonViewerOpen, setIsJsonViewerOpen] = React.useState(false);
  const [isComposerOpen, setIsComposerOpen] = React.useState(false);
  const [composerMode, setComposerMode] = React.useState<
    'new' | 'reply' | 'reply-single' | 'forward'
  >('reply');
  const [isOpeningDraft, setIsOpeningDraft] = React.useState(false);

  const cmailContext = useCmailContext();
  const [messageData, setMessageData] = useState(message);
  const [isReprocessing, setIsReprocessing] = React.useState(false);
  const [isSubmissionStatusModalOpen, setIsSubmissionStatusModalOpen] =
    React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(Date.now());
  const extractionPanel = useExtractionPanelOptional();

  const refreshTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const pollIntervalRef = React.useRef<NodeJS.Timeout | null>(null);
  const cleanupTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const handleAPIError = useAPIErrorHandler();
  const notifications = useNotifications();

  const latestDraftQuery = useLatestDraft(message.msgId);
  const hasDraft =
    latestDraftQuery.data && Object.keys(latestDraftQuery.data).length > 0;

  const isSentMail = message.emailDirection?.toUpperCase() === 'SENT';

  const hasRequestTrackingCategory = React.useMemo(() => {
    if (Array.isArray(messageData.msgCategory)) {
      return messageData.msgCategory.some(tracksRequests);
    }
    return tracksRequests(messageData.msgCategory);
  }, [messageData.msgCategory]);

  const shouldShowRequestTracking = hasRequestTrackingCategory && !isSentMail;

  const requestTrackingQuery = useRequestTracking(
    messageData.msgId,
    shouldShowRequestTracking
  );

  const getThreadLabel = () => {
    if (!threadContext) {
      return '';
    }
    if (threadContext.isCurrentMessage) {
      return '';
    }
    const depthInfo =
      threadContext.depth && threadContext.depth > 0
        ? ` (Level ${threadContext.depth + 1})`
        : '';
    return `Previous ${threadContext.messageIndex}${depthInfo}`;
  };

  const handleOpenJsonViewer = () => {
    setIsJsonViewerOpen(true);
  };

  const handleCloseJsonViewer = () => {
    setIsJsonViewerOpen(false);
  };

  const handleReply = () => {
    setComposerMode('reply-single');
    setIsOpeningDraft(false);
    setIsComposerOpen(true);
  };

  const handleReplyAll = () => {
    setComposerMode('reply');
    setIsOpeningDraft(false);
    setIsComposerOpen(true);
  };

  const handleForward = () => {
    setComposerMode('forward');
    setIsOpeningDraft(false);
    setIsComposerOpen(true);
  };

  const handleOpenDraft = () => {
    if (latestDraftQuery.data && cmailContext?.goToComposerView) {
      cmailContext.goToComposerView(latestDraftQuery.data);
    }
  };

  const handleReprocessClick = () => {
    CustomModal.show(CmailReprocessConfirmationModalConfig, {
      data: {
        message: messageData,
        onConfirm: handleReprocessEmail,
        isReprocessing,
      },
    });
  };

  const handleExtractionProgressClick = () => {
    if (extractionPanel) {
      extractionPanel.togglePanel(messageData.msgId, messageData);
    }
  };

  const handleSubmissionStatusClick = React.useCallback(() => {
    setIsSubmissionStatusModalOpen(true);
  }, []);

  const handleReprocessEmail = async (
    selectedCategory: string,
    selectedSubCategory?: string
  ) => {
    if (isReprocessing) {
      return;
    }

    setIsReprocessing(true);

    try {
      await reprocessEmail({
        data: {
          msgId: messageData.msgId,
          category: selectedCategory,
          subCategory: selectedSubCategory,
        },
      });

      notifications.success('Email reprocessing initiated successfully');

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }

      refreshTimeoutRef.current = setTimeout(() => {
        pollIntervalRef.current = setInterval(async () => {
          try {
            const refreshResponse = await fetch(
              `/api/docmessage/v1/message?msgId=${messageData.msgId}`
            );
            if (refreshResponse.ok) {
              const updatedMessage = await refreshResponse.json();
              setMessageData(updatedMessage);

              if (pollIntervalRef.current) {
                clearInterval(pollIntervalRef.current);
                pollIntervalRef.current = null;
              }
              if (cleanupTimeoutRef.current) {
                clearTimeout(cleanupTimeoutRef.current);
                cleanupTimeoutRef.current = null;
              }
            }
          } catch (error) {
            if (pollIntervalRef.current) {
              clearInterval(pollIntervalRef.current);
              pollIntervalRef.current = null;
            }
            if (cleanupTimeoutRef.current) {
              clearTimeout(cleanupTimeoutRef.current);
              cleanupTimeoutRef.current = null;
            }
          }
        }, 5000);

        cleanupTimeoutRef.current = setTimeout(() => {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          cleanupTimeoutRef.current = null;
        }, 60000);
      }, 2000);
    } catch (error) {
      handleAPIError('Failed to reprocess email. Please try again.')(
        error as Error
      );
    } finally {
      setIsReprocessing(false);
    }
  };

  useEffect(() => {
    setMessageData(message);
  }, [message]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
        cleanupTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const hasExtractedData =
      messageData.extractedData && messageData.extractedData.trim() !== '';

    if (!hasExtractedData) {
      const pollInterval = setInterval(async () => {
        try {
          const response = await fetch(
            `/api/docmessage/v1/message?msgId=${messageData.msgId}`
          );
          if (response.ok) {
            const updatedMessage = await response.json();
            if (
              updatedMessage.extractedData &&
              updatedMessage.extractedData.trim() !== ''
            ) {
              setMessageData(updatedMessage);
              clearInterval(pollInterval);
            }
          }
        } catch (error) {
          // Silently handle polling errors
        }
      }, 15000);

      return () => clearInterval(pollInterval);
    }
  }, [messageData.msgId, messageData.extractedData]);

  const hasExtractedData =
    messageData.extractedData && messageData.extractedData.trim() !== '';

  const isSubmission = React.useMemo(() => {
    return Array.isArray(messageData.msgCategory)
      ? messageData.msgCategory?.some(
          (category) =>
            typeof category === 'string' &&
            category.toLowerCase() === 'submission'
        )
      : typeof messageData.msgCategory === 'string' &&
          messageData.msgCategory.toLowerCase() === 'submission';
  }, [messageData.msgCategory]);

  const isRenewal = React.useMemo(() => {
    const check = (cat: string) =>
      cat.toLowerCase() === 'renewal' || cat.toLowerCase() === 'renewals';
    return Array.isArray(messageData.msgCategory)
      ? messageData.msgCategory.some(
          (category) => typeof category === 'string' && check(category)
        )
      : typeof messageData.msgCategory === 'string' &&
          check(messageData.msgCategory);
  }, [messageData.msgCategory]);

  const isBOR = React.useMemo(() => {
    return Array.isArray(messageData.msgCategory)
      ? messageData.msgCategory.includes('BOR')
      : messageData.msgCategory === 'BOR';
  }, [messageData.msgCategory]);

  const submissionQuery = useGetSubmissionByMsgId(
    messageData.msgId,
    isSubmission || isRenewal
  );
  const submissionNumber = submissionQuery.data?.submissionNumber;
  const submissionId = submissionQuery.data?.id;

  const { missingFields, missingFieldsCount } = React.useMemo(() => {
    let fields: Record<string, any> = {};
    let count = 0;
    if (messageData.extractedData) {
      try {
        const parsed = JSON.parse(messageData.extractedData);
        if (parsed.missingMandatoryFields) {
          fields = parsed.missingMandatoryFields;
          count = Object.keys(fields).length;
        }
      } catch (e) {
        // ignore
      }
    }
    return { missingFields: fields, missingFieldsCount: count };
  }, [messageData.extractedData]);

  const dynamicDescription = React.useMemo(() => {
    const subCategory = messageData.emailSubCategory;
    const subSubCategories = messageData.emailSubSubCategory;

    if (subCategory && subSubCategories && subSubCategories.length > 0) {
      return `Fields required for ${subCategory} submission (${subSubCategories.join(
        ', '
      )})`;
    }
    if (subCategory) {
      return `Fields required for ${subCategory} submission`;
    }
    return 'Fields required for submission';
  }, [messageData.emailSubCategory, messageData.emailSubSubCategory]);

  const warningsCount = React.useMemo(() => {
    return fullMessageDetails.warnings?.length || 0;
  }, [fullMessageDetails.warnings]);

  const formatProcessedAt = React.useMemo(() => {
    if (!messageData.extractedData) return null;

    try {
      const parsed = JSON.parse(messageData.extractedData);
      if (parsed.processedAt_utc) {
        const processedDate = new Date(parsed.processedAt_utc);

        if (isNaN(processedDate.getTime())) {
          return null;
        }

        const now = new Date(currentTime);
        const diffMs = now.getTime() - processedDate.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        let emoji;
        if (diffMinutes < 1) {
          emoji = '🚀';
        } else if (diffMinutes < 60) {
          emoji = '⚡';
        } else {
          emoji = '🕒';
        }

        const exactTime = processedDate.toLocaleString(undefined, {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZoneName: 'short',
        });

        return `${emoji} ${exactTime}`;
      }
    } catch (e) {
      // ignore parsing errors
    }
    return null;
  }, [messageData.extractedData, currentTime]);

  const shouldShowSubmissionStatus =
    missingFieldsCount > 0 ||
    messageData.failureType ||
    (shouldShowRequestTracking &&
      requestTrackingQuery.data?.data &&
      requestTrackingQuery.data.data.msgId &&
      requestTrackingQuery.data.data.requestStatus) ||
    warningsCount > 0;

  // A sub-sub-category that repeats the sub-category's label is not shown.
  const subSubCategories = distinctSubSubCategories(
    messageData.emailSubCategory,
    messageData.emailSubSubCategory,
    getCategoryAbbreviation
  );

  return (
    <>
      {shouldShowSubmissionStatus && (
        <CmailStatusBanner
          missingFieldsCount={missingFieldsCount}
          failureType={messageData.failureType}
          trackingData={
            shouldShowRequestTracking ? requestTrackingQuery.data?.data : null
          }
          warningsCount={warningsCount}
          onClick={handleSubmissionStatusClick}
          jiraTicketNumber={messageData.jiraTicketNumber}
          category={messageData.msgCategory}
        />
      )}
      <Paper className={classes.container} shadow={undefined}>
        <Box className={classes.content}>
          {messageData.emailType && (
            <Box className={classes.section}>
              <FontAwesomeIcon icon={faEnvelope} className={classes.icon} />
              <Text className={classes.label}>Type:</Text>
              <Badge
                size="sm"
                className={`${classes.chip} ${classes.emailTypeBadge}`}
                style={
                  {
                    '--badge-color': getEmailTypeColor(messageData.emailType),
                  } as React.CSSProperties
                }
              >
                {messageData.emailType}
              </Badge>
            </Box>
          )}

          <Box className={classes.section}>
            <FontAwesomeIcon icon={faGrid2} className={classes.icon} />
            <Text className={classes.label}>Category:</Text>
            <Badge
              size="sm"
              variant="outline"
              className={`${classes.chip} ${classes.categoryBadge}`}
              style={
                {
                  '--badge-color': getCategoryChipStyle(messageData.msgCategory)
                    .color,
                } as React.CSSProperties
              }
            >
              {getDisplayCategory(messageData.msgCategory)}
            </Badge>
          </Box>

          {messageData.emailSubCategory && (
            <Badge
              size="sm"
              variant="outline"
              className={`${classes.chip} ${classes.subCategoryBadge}`}
            >
              {getCategoryAbbreviation(messageData.emailSubCategory)}
            </Badge>
          )}

          {subSubCategories.length > 0 && (
            <Box className={classes.section}>
              {subSubCategories.map((subSubCategory, index) => (
                <Badge
                  key={index}
                  size="sm"
                  className={`${classes.chip} ${classes.subSubCategoryBadge}`}
                >
                  {getCategoryAbbreviation(subSubCategory)}
                </Badge>
              ))}
            </Box>
          )}

          <Box className={classes.rightSection}>
            {(isSubmission || isRenewal) &&
              submissionQuery.isSuccess &&
              submissionNumber &&
              submissionId && (
                <Link
                  to={buildSubmissionDetailsLink(
                    '/admin',
                    submissionId,
                    Boolean(submissionQuery.data?.isRenewal)
                  )}
                  className={classes.submissionLink}
                >
                  Go to{' '}
                  <b className={classes.submissionLinkBold}>
                    {submissionNumber}
                  </b>
                </Link>
              )}
            <CmailClaimLink extractedData={messageData.extractedData} />
            {threadContext && getThreadLabel() && (
              <Text size="sm" className={classes.threadLabel}>
                {getThreadLabel()}
              </Text>
            )}
            {!isSentMail && (
              <Box className={classes.assignedToWrapper}>
                <AssignedToCell
                  msgId={fullMessageDetails.msgId}
                  assignedTo={fullMessageDetails.assignedTo}
                />
              </Box>
            )}
            {hasDraft && (
              <Tooltip label="Open Draft">
                <ActionIcon
                  variant="subtle"
                  onClick={handleOpenDraft}
                  size="lg"
                  className={classes.draftButton}
                >
                  <FontAwesomeIcon icon={faPen} className={classes.draftIcon} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="Reply All">
              <ActionIcon
                variant="subtle"
                onClick={handleReplyAll}
                size="lg"
                color="gray"
              >
                <FontAwesomeIcon icon={faReplyAll} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Reply">
              <ActionIcon
                variant="subtle"
                onClick={handleReply}
                size="lg"
                color="gray"
              >
                <FontAwesomeIcon icon={faReply} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Forward">
              <ActionIcon
                variant="subtle"
                onClick={handleForward}
                size="lg"
                color="gray"
              >
                <FontAwesomeIcon
                  icon={faReply}
                  className={classes.forwardIcon}
                />
              </ActionIcon>
            </Tooltip>
            {!isSentMail && (
              <>
                {hasExtractedData ? (
                  <>
                    <Tooltip
                      label={
                        <div>
                          <div>View Extracted Data</div>
                          {formatProcessedAt && (
                            <div className={classes.processedAtHint}>
                              {formatProcessedAt}
                            </div>
                          )}
                        </div>
                      }
                    >
                      <ActionIcon
                        variant="subtle"
                        onClick={handleOpenJsonViewer}
                        size="lg"
                        color="gray"
                      >
                        <FontAwesomeIcon icon={faBracketsCurly} />
                      </ActionIcon>
                    </Tooltip>
                    <Tooltip label="View Extraction Progress">
                      <ActionIcon
                        variant="subtle"
                        onClick={handleExtractionProgressClick}
                        size="lg"
                        color="gray"
                      >
                        <FontAwesomeIcon icon={faChartLine} />
                      </ActionIcon>
                    </Tooltip>
                  </>
                ) : (
                  <Tooltip label="Data Extraction in Progress">
                    <ActionIcon
                      variant="subtle"
                      onClick={handleExtractionProgressClick}
                      size="lg"
                      color="gray"
                    >
                      <InProgressIcon color="var(--mantine-color-cyan-6)" />
                    </ActionIcon>
                  </Tooltip>
                )}
                <Tooltip label="Reprocess Email">
                  <ActionIcon
                    variant="subtle"
                    onClick={handleReprocessClick}
                    disabled={isReprocessing}
                    size="lg"
                    color="gray"
                    className={
                      isReprocessing ? classes.reprocessSpin : undefined
                    }
                  >
                    <FontAwesomeIcon icon={faArrowsRotate} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
            {threadContext && (
              <Tooltip
                label={
                  threadContext.isExpanded
                    ? 'Collapse Message'
                    : 'Expand Message'
                }
              >
                <ActionIcon
                  variant="subtle"
                  onClick={threadContext.onToggleExpansion}
                  size="lg"
                  color="gray"
                >
                  {threadContext.isExpanded ? (
                    <FontAwesomeIcon icon={faChevronUp} />
                  ) : (
                    <FontAwesomeIcon icon={faChevronDown} />
                  )}
                </ActionIcon>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Paper>

      <JsonViewerModal
        open={isJsonViewerOpen}
        onClose={handleCloseJsonViewer}
        data={(() => {
          if (!messageData.extractedData) return null;
          try {
            return JSON.parse(messageData.extractedData);
          } catch {
            return null;
          }
        })()}
      />

      <CmailComposer
        open={isComposerOpen}
        onClose={() => {
          setIsComposerOpen(false);
          setIsOpeningDraft(false);
        }}
        mode={composerMode}
        message={
          isOpeningDraft && hasDraft
            ? latestDraftQuery.data
            : fullMessageDetails
        }
        msgCategory={messageData.msgCategory}
        missingFields={missingFields}
        missingFieldsDescription={dynamicDescription}
      />

      <CmailStatusModal
        open={isSubmissionStatusModalOpen}
        onClose={() => setIsSubmissionStatusModalOpen(false)}
        missingFields={missingFields}
        missingFieldsCount={missingFieldsCount}
        missingFieldsDescription={dynamicDescription}
        failureType={messageData.failureType}
        trackingData={
          shouldShowRequestTracking ? requestTrackingQuery.data?.data : null
        }
        warnings={fullMessageDetails.warnings || []}
        jiraTicketNumber={messageData.jiraTicketNumber}
        category={messageData.msgCategory}
        onRequestMissingInfo={() => {
          setComposerMode('reply');
          setIsOpeningDraft(false);
          setIsComposerOpen(true);
        }}
        onProvideMissingInfo={() => {
          setComposerMode('forward');
          setIsOpeningDraft(false);
          setIsComposerOpen(true);
        }}
        onRefreshTracking={
          shouldShowRequestTracking
            ? () => requestTrackingQuery.refetch()
            : undefined
        }
        isRefreshingTracking={
          shouldShowRequestTracking ? requestTrackingQuery.isFetching : false
        }
        onCreateSalesforceCase={() => {
          // TODO: Implement Salesforce case creation
          console.log('Create Salesforce Case clicked');
        }}
      />
    </>
  );
};
